import { Spinner, Text, makeStyles, tokens } from '@coreai/fluentui-react';
import { ErrorCircleRegular } from '@fluentui/react-icons';

/**
 * Standard loading / error / empty placeholder used by panel list bodies
 * (AgentMcpView, AgentSubworkflowView, etc.).
 *
 * Pulled into a single component so every list surface shares identical
 * padding, icon sizing, and color treatment.
 */

type PanelStatusVariant = 'loading' | 'error' | 'empty';

export interface PanelStatusProps {
  variant: PanelStatusVariant;
  /** Primary message line. */
  message: string;
  /** Optional secondary line under the primary message. */
  detail?: string;
  /** Hero icon for the 'empty' variant. Ignored for other variants. */
  icon?: JSX.Element;
}

const useStyles = makeStyles({
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeHero700,
    color: tokens.colorNeutralForeground4,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    gap: tokens.spacingVerticalS,
  },
  errorIcon: {
    fontSize: tokens.fontSizeHero800,
  },
});

export const PanelStatus = ({ variant, message, detail, icon }: PanelStatusProps) => {
  const styles = useStyles();

  if (variant === 'loading') {
    return (
      <div className={styles.base}>
        <Spinner size="medium" />
        <Text>{message}</Text>
        {detail && <Text size={200}>{detail}</Text>}
      </div>
    );
  }

  if (variant === 'error') {
    return (
      <div className={`${styles.base} ${styles.error}`}>
        <ErrorCircleRegular className={styles.errorIcon} />
        <Text>{message}</Text>
        {detail && <Text size={200}>{detail}</Text>}
      </div>
    );
  }

  return (
    <div className={`${styles.base} ${styles.empty}`}>
      {icon && <span className={styles.emptyIcon}>{icon}</span>}
      <Text>{message}</Text>
      {detail && <Text size={200}>{detail}</Text>}
    </div>
  );
};

export default PanelStatus;
